import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';
import pattern_config from '#core/pattern-config';
import expandable from '#partial/expandable/expandable';


const state = window.teal_state = window.teal_state || {};
state.masthead = state.masthead || {};
state.masthead.$page = state.masthead.$page || {};
state.masthead.$palette_tracker = state.masthead.$palette_tracker || {};

const masthead_$ = '.masthead';
const sticky_c = ':sticky-true';
const compactable_c = ':compact-true';
const root_$ = ':root';
const desktop_nav_$ = '.masthead__nav-main';
const progress_$ = '.masthead__progress';
const palette_tracker_$ = '.palette-tracker';
const page_$ = '.page-wrapper';
const target_attr = 'aria-controls';
const page_dynamic_attr = 'data-palette-dynamic';
const tracker_root_attr = 'data-palette-root';
const tracker_panel_attr = 'data-palette-panel';
const panel_palette_attr = 'data-expandable-panel-palette';
const megamenu_$ = '.expandable--megamenu';
const progress_prop = '--progress-bar-percent';

// We have to introduce a threshold into the compacting logic because
// window.scrollY changes as the height of the masthead changes
const compact_scroll_position = 300;
const compact_scroll_threshold = 32;

const dimension_props = {
	top: '--masthead-top',
	height: '--masthead-height',
};
const dimension_values = {
	top: -1,
	height: -1,
};

let has_masthead = false;
let $root = null;
let $masthead = null;
let $desktop_nav = null;
let $progress = null;
let $hero = null;
let $main = null;
let is_sticky = false;
let is_compactable = false;
let is_compacting = false;


const module = {
	nav_mobile_toggle_$: '.nav-mobile__expandable-button',
	stuck_c: 'masthead--sticky--stuck',
	compact_c: 'masthead--sticky--compact',
	
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('masthead')) return false;
		
		load.content(() => {
			has_masthead = assign_elements();
			if (!has_masthead) return false;
			
			assign_scroll_events();
			assign_resize_events();
			assign_mutation_events();
			init_check_dimensions();
		});
		load.all(() => {
			has_masthead = assign_elements();
			if (!has_masthead) return false;
		});
	},
	
	
	/**
	 * Set body palette
	 * @param {int} palette
	 */
	setBodyPalette(palette) {
		if (!state.masthead.$palette_tracker || !state.masthead.$page) return false;
		
		state.masthead.$palette_tracker.setAttribute(tracker_root_attr, palette || 0);
		state.masthead.$page.setAttribute(page_dynamic_attr, palette || 0);
	},
	
	
	/**
	 * Get masthead height
	 * @return {int}
	 */
	getHeight() {
		const body_styles = getComputedStyle(document.body);
		const height = body_styles.getPropertyValue(dimension_props.height);
		return parseInt(height || 0);
	},
	
	
	/**
	 * Is the masthead sticky?
	 * @return {boolean}
	 */
	isSticky() {
		if (!$masthead) {
			$masthead = document.querySelector(masthead_$);
		}
		return $masthead.classList.contains(sticky_c);
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_masthead) return true;
	
	$masthead = document.querySelector(masthead_$);
	if (!$masthead) return false;
	
	$root = document.querySelector(root_$);
	$desktop_nav = document.querySelector(desktop_nav_$);
	state.masthead.$palette_tracker = document.querySelector(palette_tracker_$);
	state.masthead.$page = document.querySelector(page_$) || document.body;
	
	is_sticky = $masthead.classList.contains(sticky_c);
	is_compactable = $masthead.classList.contains(compactable_c);
	
	$masthead.addEventListener(events.expandable.toggle_start, e => {
		update_panel_palette(e);
	});
	
	// Prevent click on nav item with dropdown but no link
	document.addEventListener(g.click_e, e => {
		const $non_link_nav_item = e.target.closest(
			'.nav-item__expandable-button[href="#"]'
		);
		if (!$non_link_nav_item) return;
		
		e.preventDefault();
		return false;
	});
	
	$progress = document.querySelector(progress_$);
	
	return true;
}


/**
 * Assign scroll events
 */
function assign_scroll_events() {
	const scroll_callbacks = [
		($progress && update_progress),
		check_sticky,
		check_top,
		update_sticky_height,
	].filter(x => x);
	scroll_callbacks.forEach(callback => callback());
	
	window.addEventListener(g.scroll_e, util.throttle(() => {
		scroll_callbacks.forEach(callback => callback());
	}, 200));
}


/**
 * Assign resize events
 */
function assign_resize_events() {
	const resize_callbacks = [
		check_dimensions,
	];
	resize_callbacks.forEach(callback => callback());
	
	window.addEventListener(g.resize_e, util.throttle(() => {
		resize_callbacks.forEach(callback => callback());
	}, 150));
}


/**
 * Assign DOM change events
 */
function assign_mutation_events() {
	const mutation_callbacks = [
		check_dimensions,
		check_sticky,
	];
	mutation_callbacks.forEach(callback => callback());
	
	const observer = new MutationObserver(() => {
		mutation_callbacks.forEach(callback => callback());
	});
	observer.observe($root, {
		childList: true,
		subtree: true,
	});
}


/**
 * Update masthead palette setting from visible panel
 * @param {event} e
 */
function update_panel_palette(e) {
	const $target = e.target;
	if (!$target) return false;

	// When the megamenu is positioned below the masthead (not behind it),
	// it should not affect the masthead palette.
	if (
		!pattern_config.masthead.is_megamenu_behind_masthead
		&& $target.matches(megamenu_$)
	) return false;

	const panel_palette = $target.getAttribute(panel_palette_attr);
	if (panel_palette === null || panel_palette === '') return false;

	state.masthead.$palette_tracker.setAttribute(
		tracker_panel_attr,
		(e.detail.is_active)
			? panel_palette
			: 0
	);
}


/**
 * Repeatedly check dimensions until they are set
 */
function init_check_dimensions() {
	const interval = setInterval(() => {
		if (dimension_values.height > 0) {
			clearInterval(interval);
			return false;
		}
		check_dimensions();
	}, 100);
}


/**
 * Check height of container element
 */
function check_dimensions() {
	if (!$masthead) return false;
	
	check_top();
	check_height();
}


/**
 * Get top position of element
 * @param {element} $element
 * @return {int|boolean}
 */
function get_top($element) {
	const bounds = $element.getBoundingClientRect();
	if (!bounds.width) return false;
	
	return Math.floor(bounds.top);
}


/**
 * Get height of masthead
 * @return {int}
 */
function get_height() {
	return Math.floor(layout.getHeight($masthead, false));
}


/**
 * Check top position of masthead
 */
function check_top() {
	const top = get_top($masthead);
	if (top === false) return false;
	if (top === dimension_values.top) return false;
	
	set_dimension('top', top);
}


/**
 * Check height of masthead
 */
function check_height() {
	const height = get_height();
	if (height === dimension_values.height) return false;
	
	set_dimension('height', height);
}


/**
 * Set dimension variables
 * @param {string} name
 * @param {int} value
 */
function set_dimension(name, value) {
	$root.style.setProperty(dimension_props[name], `${value}px`);
	dimension_values[name] = value;
}


/**
 * Check if masthead is sticky
 * @deprecated This method is unreliable, now uses scroll events. If enabling
 * this, also search for init_check_sticky() in masthead.scss and uncomment
 * "top: -1px". This causes the observer to know when an element is truly
 * stuck and not just starting at the top of the page by default.
 */
function init_check_sticky() {
	const sticky_observer = new IntersectionObserver(
		([entry]) => {
			const is_stuck = entry.intersectionRatio < 1;
			entry.target.classList.toggle(module.stuck_c, is_stuck);
		}, {
			rootMargin: '-1px 0px 0px 0px',
			threshold: [1],
		}
	);
	sticky_observer.observe($masthead);
}


/**
 * Check if masthead is stuck
 */
function check_sticky() {
	if (!is_sticky) return false;
	
	const top = get_top($masthead);
	if (top === false) return false;
	
	const is_stuck = top <= 0.5;
	$masthead.classList.toggle(module.stuck_c, is_stuck);
}


/**
 * Update progress bar
 */
function update_progress() {
	const window_height = window.innerHeight;
	$hero = $hero || document.querySelector('.hero');
	if (!$hero) return false;
	
	// TODO: update to use sidebar group
	$main = $main || document.querySelector('main');
	
	const hero_position_rect = $hero.getBoundingClientRect();
	const article_top = hero_position_rect.top - dimension_values.height;
	const article_height = layout.getHeight($hero) + layout.getHeight($main);
	const viewable_area = window_height - dimension_values.height;
	
	const progress = (Math.min(Math.max((
		article_top / (viewable_area - article_height)
	), 0), 1) * 100).toFixed(2);
	
	$masthead.style.setProperty(progress_prop, `${progress}%`);
}


/**
 * Update height (compactness) of sticky masthead
 */
function update_sticky_height() {
	if (!is_compactable) return false;
	if (is_compacting) return false;
	
	const is_sticky = $masthead.classList.contains(module.stuck_c);
	const is_compact = $masthead.classList.contains(module.compact_c);
	if (!is_sticky && !is_compact) return false;
	
	// If we're near the top of the page, force compact off
	if (window.scrollY < compact_scroll_position - compact_scroll_threshold) {
		if (is_compact) {
			toggle_compact(false);
		}
		return false;
	}
	
	// If masthead is already compact, do nothing
	if (is_compact) return false;
	
	// Force compact on
	if (window.scrollY >= compact_scroll_position) {
		toggle_compact(true);
	}
}


/**
 * Toggle compact masthead
 * @param {boolean} compact
 */
function toggle_compact(compact) {
	is_compacting = true;
	
	if (compact) {
		$masthead.classList.add(module.compact_c);
	} else {
		$masthead.classList.remove(module.compact_c);
	}
	$masthead.addEventListener(g.transition_end_e, function done() {
		$masthead.removeEventListener(g.transition_end_e, done);
		is_compacting = false;
		check_dimensions();
	});
	
	// Fallback in case transition end event doesn't fire
	setTimeout(() => {
		is_compacting = false;
		check_dimensions();
	}, 500);
}
